import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import * as store from '@attain-apps/fe/web/main/lib/store'
import { Word } from '@attain-apps/string-builder'
import { TypeAsserter } from '@attain-apps/type-guards'
import * as rxjs from 'rxjs'

import type { Context, CreateEntity, Entity, TypeParams } from './types'

@ngCore.Injectable({ providedIn: 'root' })
export class Api implements ec.models.Api<TypeParams> {
  /** TODO:  */
  private readonly _localStorage = new store.storage.included.LocalStorage({
    name: 'Summit.Archive.Api.LocalStorage',
    properties: {
      lookup: store.storage.lookup({
        get: (key, ids = []) => ids,
        set: (
          key: Key,
          value: Instruction,
          current: readonly Entity[] = [],
        ) => {
          if (value.add) {
            const copy = [...current]
            copy.unshift({
              ...value.add,
              date: new ents.core.dms.data.expression.instance.definitions.DmsDate(),
              id: Word.generate(),
            })
            return copy
          }
          if (value.remove) {
            return current.filter(e => e.id !== value.remove)
          }
          if (value.set) {
            const copy = [...current]
            const index = copy.findIndex(e => e.id === value.set.id)
            if (index !== -1) {
              copy[index] = value.set
            }
            return copy
          }

          return current
        },
      }),
    },
  })

  /** @inheritdoc */
  public list = ec.models.Api.list.using.nothingButContext<TypeParams>(
    context =>
      context
        ? this._localStorage.selectors
            .getFromLookup(Key.from(context))
            .asObservable.pipe(
              rxjs.map(entities =>
                entities.map(entity => ({
                  ...entity,
                  date: TypeAsserter.validate(
                    ents.core.common.value.Raw.cast(
                      entity.date,
                      ents.core.common.value.types.Intrinsic.Date,
                    ),
                    TypeAsserter.assertIsNotNullish,
                  ),
                })),
              ),
            )
        : rxjs.of([]),
  )
  /** @inheritdoc */
  public delete: ec.models.Api<TypeParams>['delete'] = (context, id) => {
    TypeAsserter.assertIsNotNullish(context, 'Context is required')

    return this._localStorage.actions.putInLookup.from('Api.Delete').execute({
      key: Key.from(context),
      value: { remove: id },
    }).asObservable
  }

  /** @inheritdoc */
  public create: ec.models.Api<TypeParams>['create'] = (context, data) => {
    TypeAsserter.assertIsNotNullish(context, 'Context is required')
    return this._localStorage.actions.putInLookup.from('Api.Create').execute({
      key: Key.from(context),
      value: { add: data },
    }).asObservable
  }

  /** @inheritdoc */
  public update: ec.models.Api<TypeParams>['update'] = (
    context,
    { id, updates },
  ) => {
    TypeAsserter.assertIsNotNullish(context, 'Context is required')
    const archive = {
      ...updates,
      id,
    }
    ents.core.summit.Archive.assertIsArchive(archive)

    return this._localStorage.actions.putInLookup.from('Api.Update').execute({
      key: Key.from(context),
      value: { set: archive },
    }).asObservable
  }
}

type Key<
  TTenantId extends string = string,
  TZoneId extends string = string,
> = `${TTenantId}.${TZoneId}`
namespace Key {
  /** Create LocalStorage key from context */
  export function from(context: NonNullable<Context>): Key {
    return `${context.tenantId}.${context.zoneId}`
  }
}

export type Instruction = ExactlyOneOf<{
  readonly add: CreateEntity
  readonly set: Entity
  readonly remove: Entity['id']
}>
